/**
 * Centralized Configuration - Environment-driven with zero hardcoded tenant fallbacks
 */

const getBoolEnv = (val, defaultVal = false) => {
  if (val === undefined || val === null || val === '') return defaultVal;
  return val === 'true' || val === true;
};

export const ENV = {
  // Mock Data Toggle - False by default for live API communication
  USE_MOCK: getBoolEnv(process.env.REACT_APP_USE_MOCK, false),

  // Platform & Authentication Service (Port 8080)
  PLATFORM_BACKEND_URL: process.env.REACT_APP_PLATFORM_BACKEND_URL || 'http://localhost:8080',
  AUTH_BASE_PATH: process.env.REACT_APP_AUTH_BASE_PATH || '/platform/auth',

  // PMS Clinical Backend (Port 8081)
  BACKEND_URL: process.env.REACT_APP_BACKEND_URL || 'http://localhost:8081',
  API_BASE_PATH: process.env.REACT_APP_API_BASE_PATH || '/pms',

  // Regional & UI Settings
  CURRENCY: process.env.REACT_APP_CURRENCY || '₹',
  LOCALE: process.env.REACT_APP_LOCALE || 'en-IN',
  TIMEZONE: process.env.REACT_APP_TIMEZONE || 'Asia/Kolkata',
  DEFAULT_PAGE_SIZE: parseInt(process.env.REACT_APP_DEFAULT_PAGE_SIZE || '10', 10),
};

export const API_BASE_URL = `${ENV.BACKEND_URL}${ENV.API_BASE_PATH}`;
export const AUTH_BASE_URL = `${ENV.PLATFORM_BACKEND_URL}${ENV.AUTH_BASE_PATH}`;
export const USE_MOCK = ENV.USE_MOCK;

// Session Storage Keys
export const SESSION_KEYS = {
  HOSPITAL_ID: 'pulseflow_hospital_id',
  HOSPITAL_NAME: 'pulseflow_hospital_name',
  CLINIC_ID: 'pulseflow_clinic_id',
  CLINIC_NAME: 'pulseflow_clinic_name',
};

/**
 * Persist active tenant context into sessionStorage (isolated per tab)
 */
export const setSessionHospital = (id, name = '') => {
  if (typeof window !== 'undefined' && window.sessionStorage && id) {
    window.sessionStorage.setItem(SESSION_KEYS.HOSPITAL_ID, String(id).trim());
    if (name) {
      window.sessionStorage.setItem(SESSION_KEYS.HOSPITAL_NAME, String(name).trim());
    }
  }
};

export const setSessionClinic = (id, name = '') => {
  if (typeof window !== 'undefined' && window.sessionStorage && id) {
    window.sessionStorage.setItem(SESSION_KEYS.CLINIC_ID, String(id).trim());
    if (name) {
      window.sessionStorage.setItem(SESSION_KEYS.CLINIC_NAME, String(name).trim());
    }
  }
};

/**
 * Dynamically resolves the active hospital ID using strict priority lookup:
 * 1. Explicitly passed parameter (if valid)
 * 2. Active URL Route (/hospital/:hospitalId/...)
 * 3. Browser tab's sessionStorage ('pulseflow_hospital_id')
 * 4. localStorage verified hospital or user profile
 *
 * If none found: THROWS an Error (Strict validation: NO hardcoded fallbacks).
 */
export const getActiveHospitalId = (explicitHospitalId) => {
  // 1. Explicit parameter
  if (explicitHospitalId && explicitHospitalId !== 'hosp-1' && explicitHospitalId !== 'undefined') {
    const id = String(explicitHospitalId).trim();
    if (id) {
      setSessionHospital(id);
      return id;
    }
  }

  // 2. URL Path (/hospital/:hospitalId/...)
  if (typeof window !== 'undefined' && window.location?.pathname) {
    const match = window.location.pathname.match(/\/hospital\/([^/]+)/);
    if (match && match[1] && match[1] !== 'hosp-1' && match[1] !== 'undefined') {
      const id = match[1].trim();
      setSessionHospital(id);
      return id;
    }
  }

  // 3. Tab sessionStorage
  if (typeof window !== 'undefined' && window.sessionStorage) {
    try {
      const sessionHospId = window.sessionStorage.getItem(SESSION_KEYS.HOSPITAL_ID);
      if (sessionHospId && sessionHospId !== 'hosp-1' && sessionHospId !== 'undefined') {
        return sessionHospId.trim();
      }
    } catch (e) {}
  }

  // 4. localStorage fallback
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      const savedHosp = window.localStorage.getItem('pulseflow_pms_hospital');
      if (savedHosp) {
        const parsed = JSON.parse(savedHosp);
        if (parsed?.id && parsed.id !== 'hosp-1' && parsed.id !== 'undefined') {
          const id = String(parsed.id).trim();
          setSessionHospital(id, parsed.name);
          return id;
        }
      }
      const savedUser = window.localStorage.getItem('pms_user');
      if (savedUser) {
        const parsed = JSON.parse(savedUser);
        if (parsed?.hospitalId && parsed.hospitalId !== 'hosp-1' && parsed.hospitalId !== 'undefined') {
          const id = String(parsed.hospitalId).trim();
          setSessionHospital(id);
          return id;
        }
      }
    } catch (e) {}
  }

  // Mandatory field validation: No hardcoded fallback
  throw new Error(
    'Hospital ID is mandatory for this operation. No active hospital context was found in the URL route or session storage.'
  );
};

/**
 * Dynamically resolves the active clinic ID using strict priority lookup:
 * 1. Explicitly passed parameter (if valid)
 * 2. Active URL Route (/clinic/:clinicId/...)
 * 3. Browser tab's sessionStorage ('pulseflow_clinic_id')
 * 4. localStorage ('pms_active_clinic')
 *
 * If none found: THROWS an Error (Strict validation: NO hardcoded fallbacks).
 */
export const getActiveClinicId = (explicitClinicId) => {
  // 1. Explicit parameter
  if (explicitClinicId && explicitClinicId !== 'c-opd' && explicitClinicId !== 'all' && explicitClinicId !== 'undefined') {
    const id = String(explicitClinicId).trim();
    if (id) {
      setSessionClinic(id);
      return id;
    }
  }

  // 2. URL Path (/clinic/:clinicId/...)
  if (typeof window !== 'undefined' && window.location?.pathname) {
    const match = window.location.pathname.match(/\/clinic\/([^/]+)/);
    if (match && match[1] && match[1] !== 'c-opd' && match[1] !== 'undefined') {
      const id = match[1].trim();
      setSessionClinic(id);
      return id;
    }
  }

  // 3. Tab sessionStorage
  if (typeof window !== 'undefined' && window.sessionStorage) {
    try {
      const sessionClinicId = window.sessionStorage.getItem(SESSION_KEYS.CLINIC_ID);
      if (sessionClinicId && sessionClinicId !== 'c-opd' && sessionClinicId !== 'undefined') {
        return sessionClinicId.trim();
      }
    } catch (e) {}
  }

  // 4. localStorage fallback
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      const savedClinic = window.localStorage.getItem('pms_active_clinic');
      if (savedClinic && savedClinic !== 'c-opd' && savedClinic !== 'undefined') {
        const id = String(savedClinic).trim();
        setSessionClinic(id);
        return id;
      }
    } catch (e) {}
  }

  // Mandatory field validation: No hardcoded fallback
  throw new Error(
    'Clinic ID is mandatory for this operation. No active clinic context was found in the URL route or session storage.'
  );
};

export default ENV;
